import React from "react";
import { Copy } from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";
import { toast } from "@/design-system/components/Toast/Toast";
import { ToggleChoice } from "@/design-system/components/ToggleChoice/ToggleChoice";
import { Typography } from "@/design-system/components/Typography/Typography";
import useCurrentEnvironment from "@/hooks/use-environment";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import { jsSnippet } from "@/sources/JsSnippet";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { InstallMethod, InstallSdkStepProps } from "../types";

const methodOptions: { value: InstallMethod; label: string }[] = [
  { value: "head", label: "Site code" },
  { value: "gtm", label: "Google Tag Manager" },
];

const instructions: Record<InstallMethod, React.ReactNode[]> = {
  head: [
    "Copy the snippet below.",
    "Paste it inside the <head> tag of every page, as high up as you can.",
    "Publish the change to your site.",
  ],
  gtm: [
    "Copy the snippet below.",
    <>
      In your Google Tag Manager container, go to <strong>Tags</strong> and
      click <strong>New</strong>.
    </>,
    <>
      Under <strong>Tag Configuration</strong>, choose{" "}
      <strong>Custom HTML</strong> and paste the snippet into the HTML box.
    </>,
    <>
      Under <strong>Triggering</strong>, choose{" "}
      <strong>Initialization - All Pages</strong> so Freshpaint loads before
      your other tags fire. The <strong>All Pages</strong> trigger works too.
    </>,
    <>
      Under <strong>Advanced Settings</strong>, leave{" "}
      <strong>Consent Settings</strong> at{" "}
      <strong>No additional consent required</strong>. Freshpaint handles
      consent through its own consent settings, and a Tag Manager consent
      requirement can keep the snippet from loading at all.
    </>,
    <>
      Name the tag, for example “Freshpaint snippet”, and click{" "}
      <strong>Save</strong>.
    </>,
    <>
      Click <strong>Preview</strong> to check the tag fires on your site, then
      click <strong>Submit</strong> and <strong>Publish</strong>.
    </>,
  ],
};

export function AddSnippetStep({
  data,
  updateData,
  goNext,
}: InstallSdkStepProps) {
  const environment = useCurrentEnvironment();
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const snippet = environment
    ? jsSnippet(environment, accountMetadata, flag)
    : "";

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Copied the snippet");
    } catch {
      toast.error("Couldn’t copy. Select the snippet and copy it instead.");
    }
  }

  return (
    <WizardStepLayout
      title="Add Freshpaint to your site"
      description="The Freshpaint snippet loads the SDK, records page views and automatically tracks clicks and form submissions."
      primaryAction={{ label: "I’ve added it", onClick: goNext }}
    >
      <ToggleChoice
        className="w-fit"
        ariaLabel="Install with"
        options={methodOptions}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        value={methodOptions.find((option) => option.value === data.method)}
        handleChange={(option) => updateData({ method: option.value })}
      />
      <ol className="text-body-md text-textPrimary flex list-decimal flex-col gap-1 pl-5">
        {instructions[data.method].map((instruction, index) => (
          <li key={index}>{instruction}</li>
        ))}
      </ol>
      {data.method === "gtm" && (
        <Typography variant="body-sm" color="subtle">
          Add the snippet in one place only. If it’s already in your site code,
          don’t add it in Tag Manager too.
        </Typography>
      )}
      <div className="border-borderSubtle flex flex-col rounded-lg border">
        <div className="border-borderSubtle flex items-center justify-between gap-2 border-b px-4 py-2">
          <Typography variant="label-sm">Freshpaint snippet</Typography>
          <Button
            variant="outline"
            size="sm"
            leadingIcon={<Copy aria-hidden />}
            onClick={copySnippet}
          >
            Copy snippet
          </Button>
        </div>
        <pre
          aria-label="Freshpaint snippet"
          className="bg-surface-muted text-textPrimary text-body-xs max-h-60 overflow-auto rounded-b-lg px-4 py-3 font-mono break-all whitespace-pre-wrap"
        >
          {snippet}
        </pre>
      </div>
      <Typography variant="body-sm" color="subtle">
        Installing in a mobile app or on a server instead? Find every SDK on the{" "}
        <WizardLink
          to="/sources"
          className="text-textActionSelected cursor-pointer underline"
        >
          Sources page
        </WizardLink>
        .
      </Typography>
    </WizardStepLayout>
  );
}
